/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress, SxProps } from '@mui/material';
import { debounce } from '@mui/material/utils';

import { useForm } from '@carcareers/contexts';

export interface AutocompleteSearchOption {
  label?: string;
  value?: any;
}

export interface SearchParams {
  q: string;
  page?: number;
  limit?: number;
  [key: string]: any;
}

export interface AutocompleteSearchProps {
  name: string;
  search: (params: SearchParams) => Promise<any>;
  loading?: boolean;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  transformResult?: (item: any) => AutocompleteSearchOption;
  defaultValue?: AutocompleteSearchOption;
  sx?: SxProps;
}

export const AutocompleteSearch: FC<AutocompleteSearchProps> = ({
  name,
  sx,
  search,
  transformResult,
  label = '',
  required = false,
  loading = false,
  disabled = false,
  defaultValue = null,
}) => {
  const { setData, getData, getErrors } = useForm();
  const [value, setValue] = useState<AutocompleteSearchOption | null>(defaultValue);
  const [inputValue, setInputValue] = useState(defaultValue?.label || '');
  const [searchResults, setSearchResults] = useState<AutocompleteSearchOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const initializedRef = useRef(false);
  const userHasEditedRef = useRef(false);
  const searchTermRef = useRef<string>('');

  const truncateLabel = (label: string) => (label.length > 42 ? `${label.substring(0, 42)}...` : label);

  const handleChange = (_: React.SyntheticEvent, newValue: AutocompleteSearchOption | null) => {
    setValue(newValue);
    setData(name, newValue?.value);
    userHasEditedRef.current = true;
  };

  const handleInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    if (newInputValue && newInputValue !== defaultValue?.label) {
      userHasEditedRef.current = true;
    }
  };

  useEffect(() => {
    if (defaultValue?.value && !initializedRef.current) {
      setData(name, defaultValue.value);
      setValue(defaultValue);
      setInputValue(defaultValue.label || '');
      initializedRef.current = true;
    }
  }, [defaultValue, name, setData]);

  useEffect(() => {
    if (defaultValue?.value && initializedRef.current && !userHasEditedRef.current) {
      setValue(defaultValue);
      setInputValue(defaultValue.label || '');
      if (JSON.stringify(getData(name)) !== JSON.stringify(defaultValue.value)) {
        setData(name, defaultValue.value);
      }
    }
  }, [defaultValue, name, setData, getData]);

  const formatSearchResults = useCallback(
    (data: any) => {
      if (!data) return [];

      let items: any[] = [];

      if (data.items && Array.isArray(data.items)) {
        items = data.items;
      } else if (Array.isArray(data)) {
        items = data;
      } else if (data.data && Array.isArray(data.data)) {
        items = data.data;
      } else if (data.results && Array.isArray(data.results)) {
        items = data.results;
      }

      if (items.length === 0) return [];

      return items
        .map((item: any): AutocompleteSearchOption | null => {
          if (!item) return null;

          if (transformResult) {
            return transformResult(item);
          }

          const id = item.id !== undefined ? item.id : 'unknown';

          if (item.user?.email) {
            return {
              label: `${id} - ${item.user.email}`,
              value: item,
            };
          } else if (item.user?.firstName || item.user?.lastName) {
            const firstName = item.user.firstName || '';
            const lastName = item.user.lastName || '';
            return {
              label: `${id} - ${firstName} ${lastName}`.trim(),
              value: item,
            };
          } else if (item.name) {
            return {
              label: `${id} - ${item.name}`,
              value: item,
            };
          } else if (item.title) {
            return {
              label: `${id} - ${item.title}`,
              value: item,
            };
          } else if (typeof item === 'string') {
            return {
              label: item,
              value: item,
            };
          }

          return {
            label: `${id}`,
            value: item,
          };
        })
        .filter((option): option is AutocompleteSearchOption => option !== null);
    },
    [transformResult],
  );

  const safeSearch = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm) {
        setSearchResults([]);
        return;
      }

      if (searchTerm === searchTermRef.current) {
        return;
      }

      searchTermRef.current = searchTerm;

      try {
        setIsSearching(true);
        const searchParams: SearchParams = { q: searchTerm, page: 1, limit: 10 };

        let result = await search(searchParams);
        if (result && typeof result.unwrap === 'function') {
          try {
            result = await result.unwrap();
          } catch (unwrapError) {
            console.error('Error unwrapping RTK Query result:', unwrapError);
          }
        }

        if (result && result.data) {
          result = result.data;
        }

        const formattedResults = formatSearchResults(result);
        setSearchResults(formattedResults);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [search, formatSearchResults],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      if (searchTerm) {
        safeSearch(searchTerm);
      } else {
        setSearchResults([]);
      }
    }, 600),
    [safeSearch],
  );

  useEffect(() => {
    debouncedSearch(inputValue);
  }, [inputValue, debouncedSearch]);

  return (
    <Autocomplete
      fullWidth
      options={searchResults}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      loading={loading || isSearching}
      disabled={disabled}
      noOptionsText={inputValue ? 'No options found' : 'Type to search...'}
      filterOptions={(x) => x}
      getOptionLabel={(option) => truncateLabel((option as AutocompleteSearchOption).label || '')}
      isOptionEqualToValue={(option, value) =>
        option.value === value.value ||
        (typeof option.value === 'object' && typeof value.value === 'object' && option.value?.id === value.value?.id)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          disabled={disabled}
          error={!!getErrors(name)}
          helperText={getErrors(name)}
          slotProps={{
            input: {
              sx,
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
